import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Activity, X, Eye, Download, Upload, Folder, Send, Tag, Sparkles, Search, UserCheck, UserPlus, Lock, ShoppingCart, Heart } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// LocalStorage (non sessionStorage): il "ho capito" deve persistere anche
// dopo chiusura/riapertura del browser. La key include user.id per evitare
// che admin diversi sulla stessa macchina condividano il flag.
const SEEN_KEY_PREFIX = "ah_admin_whatsnew_seen:";

const ACCOUNT_LABEL = {
  trader: "Trader",
  real_estate: "Real Estate",
  studio: "Studio",
};

const ACCOUNT_COLOR = {
  trader: "text-blue-700",
  real_estate: "text-emerald-700",
  studio: "text-gold",
};

const ACTION_META = {
  project_file_upload: { label: "File caricati nei progetti", icon: Upload },
  project_file_download: { label: "File progetti scaricati", icon: Download },
  project_file_preview: { label: "File progetti visualizzati", icon: Eye },
  asset_download: { label: "Asset fornitori scaricati", icon: Download },
  asset_preview: { label: "Asset fornitori visualizzati", icon: Eye },
  project_create: { label: "Nuovi progetti creati", icon: Folder },
  project_submit: { label: "Progetti inviati per valutazione", icon: Send },
  discount_request: { label: "Richieste sconto", icon: Tag },
  contact_create: { label: "Nuovi contatti Real Estate", icon: UserPlus },
  product_reserve: { label: "Prodotti prenotati 48h", icon: Lock },
  product_release: { label: "Prenotazioni rilasciate", icon: Lock },
  product_sold: { label: "Prodotti venduti", icon: ShoppingCart },
  outlet_product_view: { label: "Schede outlet visualizzate", icon: Eye },
  outlet_public_reservation: { label: "Prenotazioni pubbliche da share page", icon: Heart },
  magic_search: { label: "Ricerche Magic Search", icon: Search },
};

function entrySummary(action, e) {
  switch (action) {
    case "project_file_upload":
      return { primary: e.filename, secondary: `${e.studio || e.user_email} · progetto: ${e.project_name || "—"}` };
    case "project_file_download":
    case "project_file_preview":
      return { primary: e.filename, secondary: `${e.studio || e.user_email} · ${e.project_name || "—"}` };
    case "asset_download":
    case "asset_preview":
      return { primary: e.asset_title, secondary: `${e.studio || e.user_email} · ${e.supplier_name || "—"}` };
    case "project_create":
      return { primary: e.project_name, secondary: `${e.studio || e.user_email}${e.client_name ? " · cliente: " + e.client_name : ""}` };
    case "project_submit":
      return { primary: e.project_name, secondary: `${e.studio || e.user_email} · in attesa di valorizzazione` };
    case "discount_request":
      return { primary: `Richiesta sconto · ${e.supplier_name || ""}`, secondary: e.studio || e.user_email };
    case "contact_create":
      return { primary: `Nuovo contatto · ${e.client_name || ""}`, secondary: e.studio || e.user_email };
    case "product_reserve":
      return { primary: `${e.product_name || "—"} · prenotato 48h`, secondary: `${e.studio || e.user_email}${e.expires_at ? " · scade " + new Date(e.expires_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}` };
    case "product_release":
      return { primary: `${e.product_name || "—"} · prenotazione rilasciata`, secondary: e.studio || e.user_email };
    case "product_sold":
      return { primary: `${e.product_name || "—"} · venduto`, secondary: `${e.buyer_email || "—"}${e.new_stock != null ? " · stock rimanente: " + e.new_stock : ""}` };
    case "outlet_product_view": {
      const brand = e.product_brand ? `${e.product_brand} · ` : "";
      const priceTxt = e.price_seen ? ` · € ${Math.round(Number(e.price_seen)).toLocaleString("it-IT")}` : "";
      return {
        primary: `${brand}${e.product_name || "—"}`,
        secondary: `${e.studio || e.user_email}${priceTxt}`,
      };
    }
    case "outlet_public_reservation": {
      const brand = e.product_brand ? `${e.product_brand} · ` : "";
      const contact = e.customer_email || e.customer_phone || e.user_email || "—";
      const cName = e.customer_name ? `${e.customer_name} · ` : "";
      return {
        primary: `🔒 ${brand}${e.product_name || "—"}`,
        secondary: `${cName}${contact}${e.share_link_name ? ` · da "${e.share_link_name}"` : ""}`,
      };
    }
    case "magic_search":
      return {
        primary: `"${e.query || ""}" su ${e.supplier_name || "—"}`,
        secondary: `${e.studio || e.user_email}${typeof e.total_matches === "number" ? " · " + e.total_matches + " risultati" : ""}`,
      };
    default:
      return { primary: action, secondary: e.studio || e.user_email };
  }
}

export default function AdminWhatsNewModal() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    // Apri il modal solo sulla dashboard /admin (root) — non sulle sotto-route
    // come /admin/imtask, /admin/assets, ecc. — per evitare overlay indesiderati.
    if (location.pathname !== "/admin") return;
    // Solo se il tab NON è specificato o è "home" (evita di coprire /admin?tab=users, ecc.)
    const currentTab = new URLSearchParams(location.search).get("tab");
    if (currentTab && currentTab !== "home") return;
    fetchAndMaybeOpen();
  }, [user, location.pathname, location.search]);

  // Apertura forzata dall'AdminLiveBell (anche fuori dalla pagina /admin)
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    function onForceOpen() { fetchAndMaybeOpen(true); }
    window.addEventListener("archihub:open-admin-whatsnew", onForceOpen);
    return () => window.removeEventListener("archihub:open-admin-whatsnew", onForceOpen);
  }, [user]);

  function fetchAndMaybeOpen(forceOpen = false) {
    api
      .get("/admin/whats-new")
      .then((r) => {
        const d = r.data || {};
        const totalActivities = d.total || 0;
        const pendingReq = (d.pending_discount_requests || []).length;
        const pendingUsr = (d.pending_users || []).length;
        const pendingContacts = (d.pending_contacts || []).length;
        const submitted = (d.submitted_projects || []).length;
        const total = totalActivities + pendingReq + submitted + pendingUsr + pendingContacts;
        if (total === 0) return;
        if (!forceOpen) {
          const seenKey = SEEN_KEY_PREFIX + (user?.id || user?.email || "anon");
          const seen = localStorage.getItem(seenKey);
          if (seen === d.since) return;
        }
        setData({ ...d, _total: total });
        setOpen(true);
      })
      .catch(() => {});
  }

  function close() {
    if (data?.since) {
      const seenKey = SEEN_KEY_PREFIX + (user?.id || user?.email || "anon");
      localStorage.setItem(seenKey, data.since);
    }
    // Notifica AdminLiveBell di azzerare il badge
    if (data?._total != null) {
      window.dispatchEvent(new CustomEvent("archihub:admin-whatsnew-seen", { detail: { total: data._total } }));
    }
    setOpen(false);
  }

  if (!open || !data) return null;

  const buckets = data.buckets || {};
  const orderedActions = [
    "project_submit",
    "project_create",
    "contact_create",
    "product_reserve",
    "product_sold",
    "product_release",
    "outlet_public_reservation",
    "outlet_product_view",
    "project_file_upload",
    "discount_request",
    "magic_search",
    "asset_preview",
    "asset_download",
    "project_file_download",
    "project_file_preview",
  ];

  const sections = orderedActions
    .filter((a) => Array.isArray(buckets[a]) && buckets[a].length > 0)
    .map((a) => ({ action: a, ...ACTION_META[a], items: buckets[a] }));

  const sinceLabel = data.since ? new Date(data.since).toLocaleString("it-IT") : null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={close}
      data-testid="admin-whatsnew-modal"
    >
      <div className="bg-cream w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-md md:rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-7 py-5 bg-ink text-cream flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Activity size={18} className="text-gold" />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-cream/60">Pannello admin</div>
              <div className="font-display text-lg">Attività dal tuo ultimo accesso</div>
              {sinceLabel && (
                <div className="text-[10px] tracking-[0.15em] uppercase text-cream/50 mt-0.5">dal {sinceLabel}</div>
              )}
            </div>
          </div>
          <button onClick={close} aria-label="Chiudi" className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm" data-testid="admin-whatsnew-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-7 space-y-6">
          {/* Pending user registrations */}
          {(data.pending_users || []).length > 0 && (
            <div data-testid="admin-whatsnew-pending-users">
              <div className="flex items-center gap-2 mb-3">
                <UserCheck size={16} className="text-gold" />
                <div className="font-display text-base text-ink">
                  Iscrizioni da approvare <span className="text-slate1 text-sm">({data.pending_users.length})</span>
                </div>
              </div>
              <div className="bg-white border border-ink/10 rounded-sm divide-y divide-ink/10">
                {data.pending_users.slice(0, 6).map((u) => (
                  <div key={u.id} className="p-3">
                    <div className="text-sm text-ink truncate">
                      {u.studio || u.name || u.email}
                      <span className={`ml-2 text-[10px] tracking-[0.2em] uppercase ${ACCOUNT_COLOR[u.account_type] || "text-gold"}`}>
                        {ACCOUNT_LABEL[u.account_type] || "Studio"}
                      </span>
                    </div>
                    <div className="text-xs text-slate1 truncate">{u.email}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Real Estate contacts */}
          {(data.pending_contacts || []).length > 0 && (
            <div data-testid="admin-whatsnew-pending-contacts">
              <div className="flex items-center gap-2 mb-3">
                <UserPlus size={16} className="text-emerald-700" />
                <div className="font-display text-base text-ink">
                  Contatti Real Estate da confermare <span className="text-slate1 text-sm">({data.pending_contacts.length})</span>
                </div>
              </div>
              <div className="bg-white border border-ink/10 rounded-sm divide-y divide-ink/10">
                {data.pending_contacts.slice(0, 6).map((c) => (
                  <div key={c.id} className="p-3">
                    <div className="text-sm text-ink truncate">
                      {c.first_name} {c.last_name}
                      <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-emerald-700">€{c.credit_amount} pending</span>
                    </div>
                    <div className="text-xs text-slate1 truncate">{c.agency || c.user_email}{c.property_address ? ` · ${c.property_address}` : ""}{c.estimated_time_label ? ` · ${c.estimated_time_label}` : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending discount requests */}
          {(data.pending_discount_requests || []).length > 0 && (
            <div data-testid="admin-whatsnew-pending-requests">
              <div className="flex items-center gap-2 mb-3">
                <Tag size={16} className="text-gold" />
                <div className="font-display text-base text-ink">
                  Richieste sconto in attesa <span className="text-slate1 text-sm">({data.pending_discount_requests.length})</span>
                </div>
              </div>
              <div className="bg-white border border-ink/10 rounded-sm divide-y divide-ink/10">
                {data.pending_discount_requests.slice(0, 6).map((r) => (
                  <div key={r.id} className="p-3">
                    <div className="text-sm text-ink truncate">{r.supplier_name}</div>
                    <div className="text-xs text-slate1 truncate">{r.studio || r.user_email}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submitted projects */}
          {(data.submitted_projects || []).length > 0 && (
            <div data-testid="admin-whatsnew-submitted">
              <div className="flex items-center gap-2 mb-3">
                <Send size={16} className="text-ink" />
                <div className="font-display text-base text-ink">
                  Progetti inviati da valorizzare <span className="text-slate1 text-sm">({data.submitted_projects.length})</span>
                </div>
              </div>
              <div className="bg-white border border-ink/10 rounded-sm divide-y divide-ink/10">
                {data.submitted_projects.slice(0, 6).map((p) => (
                  <div key={p.id} className="p-3">
                    <div className="text-sm text-ink truncate">{p.name}</div>
                    <div className="text-xs text-slate1 truncate">{p.studio || p.user_email}{p.client_name ? ` · ${p.client_name}` : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity buckets */}
          {sections.map((sec) => {
            const Icon = sec.icon || Sparkles;
            return (
              <div key={sec.action} data-testid={`admin-whatsnew-${sec.action}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={16} className="text-ink" />
                  <div className="font-display text-base text-ink">
                    {sec.label} <span className="text-slate1 text-sm">({sec.items.length})</span>
                  </div>
                </div>
                <div className="bg-white border border-ink/10 rounded-sm divide-y divide-ink/10">
                  {sec.items.slice(0, 6).map((it) => {
                    const s = entrySummary(sec.action, it);
                    return (
                      <div key={it.id} className="p-3 flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ink truncate">{s.primary}</div>
                          <div className="text-xs text-slate1 truncate">{s.secondary}</div>
                        </div>
                        <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 whitespace-nowrap">
                          {new Date(it.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    );
                  })}
                  {sec.items.length > 6 && (
                    <div className="p-3 text-xs text-slate1 italic">
                      +{sec.items.length - 6} altre attività di questo tipo.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-7 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
          {(data.pending_discount_requests || []).length > 0 && (
            <button
              onClick={() => { close(); navigate("/admin"); }}
              className="border border-ink/20 text-ink px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-cream2"
              data-testid="admin-whatsnew-go-requests"
            >
              Gestisci richieste
            </button>
          )}
          <button onClick={close} className="bg-ink text-cream px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="admin-whatsnew-dismiss">
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
}
